import ToolbarButton from "../toolbar/ToolbarButton";
import { useState, useEffect, useContext } from "react";
import { useProject } from "../../api/project";
import { StrategyContext } from "../../contexts/StrategyContext";
import { useStrategy } from "../../api/strategy";
import { useRetainedTilesUrl } from "../../hooks/useRetainedTilesUrl";
import { recalcGreyed } from "../../constants/recalcPaint";
import { Layer } from "../../types/layer";

/** The detailed tileset of finely mapped areas (see the backend's
 *  data_review/fine_tiles.py): drawn from FINE_MIN_ZOOM, cut to FINE_MAX_ZOOM. */
const FINE_MIN_ZOOM = 13;
const FINE_MAX_ZOOM = 16;

const VegetationLayer = (
  id: string,
  name: string
) => {
  const { isLoading: projectLoading } = useProject();
  const { selectedStrategy } = useContext(StrategyContext);
  const { strategy } = useStrategy();
  const [active, setActive] = useState(false);
  const [stale, setStale] = useState(false);

  const strategyData = selectedStrategy ? strategy?.data?.[selectedStrategy.id] ?? null : null;
  // The live URL disappears while a fuel recalculation is in flight; keep
  // showing the retained tiles greyed out instead of hiding the layer.
  const liveTilesUrl = strategyData?.vegetation_tiles_url as string | undefined;
  const { tilesUrl, isRetained: recalculating } =
    useRetainedTilesUrl(liveTilesUrl);
  const tileUrl = tilesUrl ?? null;
  // Areas a refinement mapped finely have a detailed tileset of their own
  // (z13-16); the main one stops at z12, about 2 m. It is drawn on top from
  // zoom 13 while the main tiles are live, and the main tiles' copies of
  // those areas (flagged ``fine``) are hidden there.
  const fineUrl = !recalculating ? ((strategyData?.vegetation_fine_tiles_url as string | null | undefined) ?? null) : null;

  const vectorSource = tileUrl
    ? {
        tiles: [tileUrl],
        minzoom: 5,
        // Tileset is generated to z12 (see PMTILES_MAX_ZOOM); declaring the
        // real max lets MapLibre overzoom z12 tiles instead of requesting
        // non-existent z13/z14 tiles (which render blank until a pan).
        maxzoom: 12,
        tileSize: 512,
        promoteId: "__id",
      }
    : null;

  useEffect(() => {
    if (vectorSource) {
      setStale(true);
    }
  }, [tileUrl, fineUrl]);
  const [fineStale, setFineStale] = useState(false);
  useEffect(() => { setFineStale(true); }, [fineUrl, active]);


  // Re-apply paint when the greyed (recalculating) state flips.
  useEffect(() => {
    if (active) setStale(true);
  }, [recalculating, active]);

  const handleToggle = () => {
    const nextActive = !active;
    setActive(nextActive);
    if (nextActive) {
      setStale(true);
    }
  };

  const layerStyle = {
    id: id,
    type: "fill" as const,
    "source-layer": "vegetation",
    paint: {
      // Base fill by vegetation-class colour. Shift-multi-select highlighting is
      // applied imperatively from Map.tsx (the vegetation highlight effect),
      // which repaints this fill for the selected polygon ids — feature-state
      // can't be used because the tiles' promoteId (`__id`) is a tile-generation
      // index, not the vegetation primary key. While recalculating, the class
      // colours are pulled toward grey but stay distinguishable.
      "fill-color": recalculating
        ? recalcGreyed(["coalesce", ["get", "color"], "#cccccc"])
        : ([
            "coalesce",
            ["get", "color"],
            "#cccccc",
          ] as const),
      "fill-opacity": fineUrl
        ? ["step", ["zoom"], 0.75, FINE_MIN_ZOOM, ["case", ["==", ["get", "fine"], 1], 0, 0.75]]
        : 0.75,
    },
    layout: {
      visibility: active ? "visible" : "none",
    },
  };

  const toolbarButton = (
      <ToolbarButton
        key={id}
        id={id}
        name={name}
        toggledOn={active}
        // Retained tiles are still loadable — keep the toggle usable while the
        // recalculation runs.
        enabled={!!tileUrl && !projectLoading}
        onClick={handleToggle}
        icon={"forest"}
      />
  )



  const computedActive = active && !!vectorSource;
  // Vegetation clicks are handled centrally in Map.tsx (single click opens the
  // edit modal; Shift+click multi-selects), so the layer only needs to declare
  // itself interactive — it has no onClick/popover of its own. No clicks while
  // greyed: the polygons are stale data.
  const interactiveLayerIds = computedActive && !recalculating ? [id] : [];

  // Purely visual: clicks land on the main layer's (transparent) copy of the
  // same polygon underneath, which carries the same attributes.
  // tileSize must be 512: Mapbox GL v3 throws on any other size for a
  // vector source ("vector tile sources must have a tileSize of 512"), and
  // the throw kept this layer off the map while the main tiles' copies of
  // the area were already hidden, leaving it blank from zoom 13.
  const fineSource = fineUrl
    ? { tiles: [fineUrl], minzoom: FINE_MIN_ZOOM, maxzoom: FINE_MAX_ZOOM, tileSize: 512, promoteId: "__id" }
    : null;
  const fineLayer: Layer = {
    id: `${id}-fine`,
    name: `${name} (detail)`,
    type: fineSource ? "vector" : undefined,
    source: fineSource,
    rawSource: fineSource,
    layerStyle: [{
      id: `${id}-fine`,
      type: "fill" as const,
      "source-layer": "vegetation",
      minzoom: FINE_MIN_ZOOM,
      paint: { "fill-color": ["coalesce", ["get", "color"], "#cccccc"], "fill-opacity": 0.75 },
      layout: { visibility: active && fineSource ? "visible" : "none" },
    }],
    toolbarButton: null,
    active: active && !!fineSource,
    stale: fineStale,
    setStale: setFineStale,
  };

  return {
    fineLayer,
    id: id,
    name: name,
    type: vectorSource ? "vector" : undefined,
    source: vectorSource,
    rawSource: vectorSource,
    layerStyle: [layerStyle],
    toolbarButton: toolbarButton,
    active: computedActive,
    stale: stale,
    setStale: setStale,
    interactiveLayerIds: interactiveLayerIds,
  };
};

export default VegetationLayer;